import { ThemeEvents } from '@theme/events';

/* ↗ .cursor/plans/WS23-analytics-gtm.md §8 — the event map, naming rules and what is deliberately not tracked. */

const PREFIX = 'rocky_';
const MAX_VALUE = 100;
const MAX_KEYS = 11; // 10 payload keys + debug_mode

/** Custom events published before Shopify.analytics is ready, replayed once it is. */
const pending = [];

/* ↗ .cursor/references/code-notes/r-analytics.js.md#§1 */
const DEBUG = (() => {
  if (document.documentElement.hasAttribute('data-r-analytics-debug')) return true;
  try {
    return window.localStorage.getItem('rAnalyticsDebug') === '1';
  } catch {
    return false;
  }
})();

/**
 * Publishes a Rocky custom event to Shopify's web-pixel bus. Nothing is delivered until a
 * custom pixel subscribes (the GTM pixel — WS23 §7.5), so this is safe to call before one
 * exists. Values are stringified and truncated; keys beyond MAX_KEYS are dropped; empty
 * values are omitted; PII never belongs here.
 *
 * @param {string} name - Event name without the `rocky_` prefix, snake_case.
 * @param {Record<string, unknown>} [payload]
 */
export function track(name, payload = {}) {
  const event = `${PREFIX}${name}`;
  const data = {};
  let keys = 0;

  for (const [key, raw] of Object.entries(payload)) {
    if (raw === undefined || raw === null || raw === '') continue;
    if (keys >= MAX_KEYS) break;
    data[key] = typeof raw === 'number' || typeof raw === 'boolean' ? raw : String(raw).slice(0, MAX_VALUE);
    keys += 1;
  }

  if (DEBUG) data.debug_mode = true;

  const bus = /** @type {any} */ (window).Shopify?.analytics;

  if (typeof bus?.publish === 'function') {
    bus.publish(event, data);
    return;
  }

  pending.push([event, data]);
}

function flushPending() {
  const bus = /** @type {any} */ (window).Shopify?.analytics;
  if (typeof bus?.publish !== 'function') return;

  for (const [event, data] of pending.splice(0)) bus.publish(event, data);
}

/**
 * Reads `data-r-track-*` parameters off an element into a flat payload.
 * `data-r-track="nav_click" data-r-track-location="footer"` → { location: 'footer' }.
 *
 * @param {HTMLElement} element
 */
function paramsFrom(element) {
  const params = {};

  for (const [key, value] of Object.entries(element.dataset)) {
    if (!key.startsWith('rTrack') || key === 'rTrack') continue;
    const name = key.slice('rTrack'.length);
    params[name.charAt(0).toLowerCase() + name.slice(1).replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)] = value;
  }

  return params;
}

/**
 * Nearest declared surface for an interaction, so one central listener can say *where*
 * a cart add or a drawer open came from. Hosts declare `data-r-track-source="plp_card"`.
 *
 * @param {EventTarget | null} target
 * @param {string} fallback
 */
function sourceOf(target, fallback) {
  const element = target instanceof Element ? target : null;
  return /** @type {HTMLElement | null} */ (element?.closest('[data-r-track-source]'))?.dataset.rTrackSource ?? fallback;
}

/** @param {string} [id] */
function numericId(id) {
  return id ? String(id).replace(/\D+/g, '') || undefined : undefined;
}

// --- Delegated markup hooks -------------------------------------------------------------

/* ↗ .cursor/references/code-notes/r-analytics.js.md#§2 */
document.addEventListener(
  'click',
  (event) => {
    const element = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-r-track]') : null
    );
    if (!element?.dataset.rTrack) return;

    const payload = paramsFrom(element);

    if (element instanceof HTMLAnchorElement && !payload.url) {
      payload.url = element.getAttribute('href') ?? undefined;
    }
    if (!payload.item && element.dataset.rTrack === 'nav_click') {
      payload.item = element.textContent?.trim();
    }

    track(element.dataset.rTrack, payload);
  },
  { capture: true, passive: true }
);

/** Accordions: `<details>` toggles do not bubble, so listen in the capture phase. */
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    // Drawers built on <details> rather than dialog-component: the mobile menu and the
    // localization picker never dispatch dialog:open.
    if (details.parentElement?.tagName === 'HEADER-DRAWER') return track('drawer_open', { drawer: 'menu' });
    if (details.closest('header-drawer')) return;
    if (details.closest('localization-form-component, drawer-localization-component, dropdown-localization-component')) {
      return track('drawer_open', { drawer: 'localization' });
    }

    const host = /** @type {HTMLElement | null} */ (details.closest('[data-r-track-section]'));
    if (!host) return;

    track('pdp_engage', {
      section: host.dataset.rTrackSection,
      product_id: document.querySelector('[data-r-track-product-id]')?.getAttribute('data-r-track-product-id'),
    });
  },
  { capture: true }
);

// --- Theme event bus ---------------------------------------------------------------------

/** The cart drawer auto-opens inside the add handler; that open is not a user opening the cart. */
let cartOpenedByAddUntil = 0;

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const { data } = /** @type {any} */ (event).detail ?? {};
  // Only additions carry a variantId; quantity changes and removals do not.
  if (!data?.variantId || data.didError) return;
  cartOpenedByAddUntil = performance.now() + 2000;

  const source =
    data.trackSource ?? sourceOf(event.target, data.source === 'product-form-component' ? 'pdp_form' : String(data.source));
  // The PDP's own buy button is the app pixel's add_to_cart; only merchandised surfaces
  // are worth a second event.
  if (source === 'pdp_form') return;

  track('quick_add', {
    source,
    product_id: numericId(data.productId),
    variant_id: numericId(data.variantId),
  });
});

document.addEventListener(ThemeEvents.cartError, (event) => {
  const { data, sourceId } = /** @type {any} */ (event).detail ?? {};
  track('cart_error', {
    code: data?.errors ? 'validation' : 'request',
    message: data?.message,
    context: sourceOf(event.target, sourceId ?? 'cart'),
  });
});

document.addEventListener(ThemeEvents.variantSelected, (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const input = /** @type {HTMLInputElement | HTMLSelectElement | null} */ (
    target?.matches('input, select') ? target : target?.querySelector('input:checked, select')
  );
  const picker = target?.closest('variant-picker, swatches-variant-picker-component, r-scent-drawer');

  const legend = input?.closest('fieldset')?.querySelector('legend')?.textContent?.trim();

  track('variant_select', {
    option_name: input?.dataset.optionName ?? legend ?? input?.name?.split('-')[0],
    option_value: input?.value,
    method: sourceOf(target, picker?.tagName.toLowerCase() === 'r-scent-drawer' ? 'scent_drawer' : 'picker'),
    product_id: numericId(target?.closest('[data-product-id]')?.getAttribute('data-product-id') ?? undefined),
    variant_id: numericId(input?.dataset.variantId),
  });
});

document.addEventListener(ThemeEvents.FilterUpdate, (event) => {
  const params = /** @type {URLSearchParams} */ (/** @type {any} */ (event).detail?.queryParams);
  if (!params) return;

  const filters = [...params.entries()].filter(([key]) => key.startsWith('filter.'));
  track('filter_apply', {
    filter_type: filters.map(([key]) => key.replace(/^filter\./, '')).join('|'),
    filter_value: filters.map(([, value]) => value).join('|'),
    sort: params.get('sort_by') ?? undefined,
    collection: window.location.pathname.split('/collections/')[1]?.split('/')[0],
  });
});

document.addEventListener(ThemeEvents.mediaStartedPlaying, () => {
  if (!document.querySelector('product-form-component')) return;
  track('pdp_engage', { section: 'video_play' });
});

document.addEventListener(ThemeEvents.zoomMediaSelected, () => {
  track('pdp_engage', { section: 'gallery_zoom' });
});

document.addEventListener(ThemeEvents.discountUpdate, (event) => {
  const cart = /** @type {any} */ (event).detail?.resource;
  const codes = cart?.discount_codes?.map((/** @type {any} */ code) => code.code).filter(Boolean);
  track('cart_extra', { action: 'discount_apply', value: codes?.join('|') });
});

/* ↗ .cursor/references/code-notes/r-analytics.js.md#§3 */
document.addEventListener(
  'dialog:open',
  (event) => {
    const host = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
    const drawer = host?.dataset.rTrackDrawer;
    if (!drawer) return;
    if (drawer === 'cart' && performance.now() < cartOpenedByAddUntil) return;

    track('drawer_open', { drawer });
  },
  { capture: true }
);

/** Carousels: user-initiated slide changes from slideshow-component. */
document.addEventListener('slideshow:select', (event) => {
  const detail = /** @type {any} */ (event).detail;
  if (!detail?.userInitiated) return;

  const host = /** @type {HTMLElement | null} */ (
    event.target instanceof Element ? event.target.closest('[data-r-track-carousel]') : null
  );
  if (!host) return;

  const action = detail.trigger === 'select' ? (detail.index > detail.previousIndex ? 'next' : 'prev') : 'swipe';
  track('carousel', {
    action,
    carousel_id: host.dataset.rTrackCarousel,
    index: detail.index,
    item_count: host.querySelectorAll('[slide], [role="listitem"], .r-product-feed__item').length || undefined,
  });
});

/* ↗ .cursor/references/code-notes/r-analytics.js.md#§4 */
const seenCarousels = new WeakSet();
/** @type {WeakMap<Element, HTMLElement>} */
const carouselHosts = new WeakMap();
const carouselObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            carouselObserver?.unobserve(entry.target);
            const host = carouselHosts.get(entry.target);
            if (!host || seenCarousels.has(host)) continue;
            seenCarousels.add(host);
            track('carousel', { action: 'view', carousel_id: host.dataset.rTrackCarousel });
          }
        },
        { threshold: 0.25 }
      )
    : null;

function observeCarousels() {
  if (!carouselObserver) return;
  for (const host of /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('[data-r-track-carousel]'))) {
    if (seenCarousels.has(host) || carouselHosts.get(host) === host) continue;
    const target = host.getClientRects().length ? host : host.querySelector('slideshow-component, media-gallery, *') ?? host;
    carouselHosts.set(target, host);
    carouselHosts.set(host, host);
    carouselObserver.observe(target);
  }
}

observeCarousels();
// Section renderer and morph bring carousels in after load; re-scan once per idle period.
let rescan = 0;
new MutationObserver(() => {
  if (rescan) return;
  rescan = (window.requestIdleCallback ?? window.setTimeout)(() => {
    rescan = 0;
    observeCarousels();
  });
}).observe(document.body, { childList: true, subtree: true });

flushPending();
document.addEventListener('DOMContentLoaded', flushPending);
window.addEventListener('load', flushPending);
